import api from './api';

// Tiny API wrapper for totals using shared axios instance
export async function fetchTotals() {
  // Module-level cache (in-flight + short TTL) so concurrent callers share a single request.
  // This is intentionally kept local to this module to avoid introducing global state elsewhere.
  //
  // Behavior:
  // - If a request is in-flight -> return the same promise
  // - If we fetched successfully recently -> return cached data
  // - If request fails -> clear in-flight and keep existing fallback behavior
  const TTL_MS = 45000; // 45s: short TTL to dedupe home critical-path calls

  if (fetchTotals.__inFlightPromise) return fetchTotals.__inFlightPromise;

  const now = Date.now();
  if (fetchTotals.__cache && now - fetchTotals.__cacheAt < TTL_MS) {
    return fetchTotals.__cache;
  }

  fetchTotals.__inFlightPromise = (async () => {
    try {
      const res = await api.get('/stats/totals');
      const data = res.data || {};
      const reviews = typeof data.reviews === 'number' ? data.reviews : 0;
      const normalized = {
        reviews,
        user: data.user || null
      };
      fetchTotals.__cache = normalized;
      fetchTotals.__cacheAt = Date.now();
      return normalized;
    } catch (err) {
      // Fallback for development if backend is unavailable
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[stats.fetchTotals] Falling back to mock due to error:', err?.message || err);
        return { reviews: 123456, user: null };
      }
      // In production, still return a safe value to avoid UI break
      return { reviews: 0, user: null };
    } finally {
      fetchTotals.__inFlightPromise = null;
    }
  })();

  return fetchTotals.__inFlightPromise;
}

// Private fields for fetchTotals() cache/in-flight de-dupe.
// Attached to the function to avoid exporting mutable module state.
fetchTotals.__inFlightPromise = null;
fetchTotals.__cache = null;
fetchTotals.__cacheAt = 0;

export const DEFAULT_SYNC_INTERVAL_MS = Number(process.env.REACT_APP_STATS_SYNC_INTERVAL_MS || 120000);
export const SIM_INCREMENT_MIN = Number(process.env.REACT_APP_SIM_INCREMENT_MIN || 1);
export const SIM_INCREMENT_MAX = Number(process.env.REACT_APP_SIM_INCREMENT_MAX || 3);
export const SIM_INTERVAL_MIN_MS = Number(process.env.REACT_APP_SIM_INTERVAL_MIN_MS || 4000);
export const SIM_INTERVAL_MAX_MS = Number(process.env.REACT_APP_SIM_INTERVAL_MAX_MS || 7000);
export const MILESTONES = [1000, 10000, 100000, 1000000];

export function formatNumber(num) {
  try {
    return Math.floor(num).toLocaleString();
  } catch {
    return String(Math.floor(num));
  }
}

// Compact formatter for counts (e.g., 1.2k, 3.4M) with clamping at 0+
export function formatCountCompact(value) {
  const n = Number.isFinite(value) ? value : 0;
  const clamped = Math.max(0, n);
  if (clamped < 1000) return String(Math.floor(clamped));
  if (clamped < 1000000) {
    const v = clamped / 1000;
    const fixed = v >= 10 ? Math.floor(v) : Math.round(v * 10) / 10;
    return `${fixed}k`;
  }
  if (clamped < 1000000000) {
    const v = clamped / 1000000;
    const fixed = v >= 10 ? Math.floor(v) : Math.round(v * 10) / 10;
    return `${fixed}M`;
  }
  const v = clamped / 1000000000;
  const fixed = v >= 10 ? Math.floor(v) : Math.round(v * 10) / 10;
  return `${fixed}B`;
}

export function getCurrentMilestoneIndex(count, milestones = MILESTONES) {
  if (!Array.isArray(milestones) || milestones.length === 0) return 0;
  for (let i = 0; i < milestones.length; i += 1) {
    if (count < milestones[i]) return i;
  }
  return milestones.length - 1; // At or beyond last milestone
}

export function getMilestoneRange(index, milestones = MILESTONES) {
  const start = index === 0 ? 0 : milestones[index - 1];
  const end = milestones[index];
  return [start, end];
}

export function getProgressPercentWithinRange(value, start, end) {
  if (end <= start) return 0;
  const clamped = Math.max(start, Math.min(value, end));
  return ((clamped - start) / (end - start)) * 100;
}


